/* Reviews use one source for rows, navigation and dialogs. No storefront API token required. */
if (!customElements.get('sa-reviews')) {
  customElements.define('sa-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialogs = [...this.querySelectorAll('[data-review-dialog]')];
      this.sortReviews();
      this.resultsObserver = new ResizeObserver(() => {
        this.updateProgramCards();
        this.updateResultsControls();
        this.updateMenuControls();
      });
      this.querySelectorAll('[data-review-results]').forEach((track) => {
        this.resultsObserver.observe(track);
        if (track.matches('[data-review-stories]')) [...track.children].forEach((story) => this.resultsObserver.observe(story));
        track.addEventListener('scroll', () => this.updateResultsControls(), { signal, passive: true });
      });
      this.querySelectorAll('[data-review-scroll]').forEach((body) => this.resultsObserver.observe(body));
      this.dialogs.filter((dialog) => dialog.querySelector('.sa-reviews__program-card')).forEach((dialog) => {
        this.resultsObserver.observe(dialog);
        this.resultsObserver.observe(dialog.querySelector('.sa-reviews__dialog-header'));
      });
      const menu = this.querySelector('[data-review-menu]');
      if (menu) {
        this.resultsObserver.observe(menu);
        menu.addEventListener('scroll', () => this.updateMenuControls(), { signal, passive: true });
      }
      const menuBar = this.querySelector('.sa-reviews__menu');
      if (menuBar) this.resultsObserver.observe(menuBar);
      this.updateMenuControls();
      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      this.addEventListener('keydown', (event) => this.handleTabKeys(event), { signal });
      this.addEventListener('input', (event) => this.updateComparison(event), { signal });
      this.addEventListener('pointerdown', (event) => {
        this.dataset.reviewInput = 'pointer';
        this.backdropStart = event.target.matches('[data-review-dialog]') && this.isOutside(event, event.target) ? event.target : null;
      }, { signal });
      this.dialogs.forEach((dialog) => {
        dialog.addEventListener('close', () => {
          if (dialog.open) return;
          document.documentElement.classList.toggle('sa-reviews-modal-open', this.dialogs.some((item) => item.open));
          dialog.reviewOpener?.focus({ preventScroll: true });
        }, { signal });
      });
      // Pause our video when another testimonial anywhere on the page starts.
      document.addEventListener('play', (event) => {
        this.querySelectorAll('video').forEach((video) => {
          if (video !== event.target) video.pause();
        });
      }, { capture: true, signal });
      window.addEventListener('hashchange', () => this.followHash(), { signal });
      window.addEventListener('hashchange:simulate', (event) => this.followHash(true, event.detail?.hash), { signal });
      // A linked review may move after sorting; realign it once the new DOM order is ready.
      this.frame = requestAnimationFrame(() => this.followHash(false));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resultsObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.dialogs?.forEach((dialog) => { if (dialog.open) dialog.close(); });
      if (!document.querySelector('sa-reviews dialog[open]')) document.documentElement.classList.remove('sa-reviews-modal-open');
    }

    sortReviews() {
      const container = this.querySelector('[data-review-rows]');
      const menu = this.querySelector('[data-review-menu]');
      if (!container || !menu) return;
      const order = (row) => {
        const number = Number(row.dataset.sortOrder);
        return Number.isFinite(number) ? number : 999999;
      };
      const rows = [...container.children].sort((a, b) => order(a) - order(b));
      const menuItems = new Map([...menu.children].map((item) => [item.querySelector('a').hash.slice(1), item]));
      rows.forEach((row) => {
        container.append(row);
        const item = menuItems.get(row.id);
        if (item) menu.append(item);
      });
    }

    handleClick(event) {
      const writtenStep = event.target.closest('[data-written-step]');
      if (writtenStep) {
        this.changeWrittenReview(writtenStep.closest('[data-written-reviews]'), Number(writtenStep.dataset.writtenStep));
        return;
      }
      const menuStep = event.target.closest('[data-menu-step]');
      if (menuStep) {
        const track = this.querySelector('[data-review-menu]');
        const item = track?.firstElementChild;
        if (!item) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        if (Math.abs(track.scrollLeft) >= track.scrollWidth - track.clientWidth - 3) {
          track.scrollTo({ left: 0, behavior: this.scrollBehavior() });
        } else {
          track.scrollBy({ left: direction * (item.getBoundingClientRect().width + gap), behavior: this.scrollBehavior() });
        }
        return;
      }
      const resultsStep = event.target.closest('[data-results-step]');
      if (resultsStep) {
        const track = resultsStep.closest('[data-results-gallery]').querySelector('[data-review-results]');
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        if (card) {
          track.scrollBy({ left: direction * Number(resultsStep.dataset.resultsStep) * (card.getBoundingClientRect().width + gap), behavior: this.scrollBehavior() });
          if (track.matches('[data-review-stories]')) {
            const body = track.closest('[data-review-scroll]');
            const dialog = body.closest('dialog');
            if (window.matchMedia('(max-width: 799px)').matches) {
              dialog.scrollTop += body.getBoundingClientRect().top - dialog.getBoundingClientRect().top;
            } else body.scrollTop = 0;
          }
        }
        return;
      }
      const opener = event.target.closest('[data-open-review]');
      if (opener) {
        const dialog = opener.closest('[data-review-row]').querySelector('[data-review-dialog]');
        if (!dialog) return;
        this.querySelectorAll('video').forEach((video) => video.pause());
        dialog.reviewOpener = opener;
        this.selectPanel(dialog, opener.dataset.openReview);
        dialog.showModal();
        this.updateProgramCards();
        document.documentElement.classList.add('sa-reviews-modal-open');
        const focusTarget = dialog.querySelector('[aria-selected="true"]') || dialog.querySelector('[data-review-panel]:not([hidden])');
        focusTarget?.focus({ preventScroll: true });
        return;
      }
      const close = event.target.closest('[data-close-review]');
      if (close) { close.closest('dialog').close(); return; }
      const tab = event.target.closest('[data-review-tab]');
      if (tab) { this.selectPanel(tab.closest('dialog'), tab.dataset.reviewTab); return; }
      const closeVideo = event.target.closest('[data-close-video]');
      if (closeVideo) { this.closeVideo(closeVideo.closest('[data-review-media]')); return; }
      const play = event.target.closest('[data-play-review]');
      if (play) { this.playVideo(play); return; }
      if (event.target === this.backdropStart && this.isOutside(event, event.target)) event.target.close();
    }

    selectPanel(dialog, key) {
      const tabs = [...dialog.querySelectorAll('[data-review-tab]')];
      const panels = [...dialog.querySelectorAll('[data-review-panel]')];
      if (!panels.some((panel) => panel.dataset.reviewPanel === key)) return;
      tabs.forEach((tab) => {
        const selected = tab.dataset.reviewTab === key;
        tab.setAttribute('aria-selected', String(selected));
        tab.classList.toggle('button--outline', selected);
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.reviewPanel !== key;
        // Hidden dialogs and clipped comparisons can defer native lazy loading indefinitely.
        // Fetch both sides when the visitor chooses this panel so sliding never reveals an unloaded image.
        if (!panel.hidden) panel.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
      });
      dialog.querySelector('[data-review-scroll]').scrollTop = 0;
      dialog.scrollTop = 0;
      this.updateProgramCards();
    }

    handleTabKeys(event) {
      delete this.dataset.reviewInput;
      if (event.key === 'Tab') {
        const dialog = event.target.closest('dialog[open]');
        if (dialog) {
          const focusable = [...dialog.querySelectorAll('button, a[href], input, select, textarea, summary, [tabindex]')]
            .filter((element) => element.tabIndex >= 0 && !element.disabled && element.getClientRects().length);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
        }
        return;
      }
      const tab = event.target.closest('[data-review-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-review-tab]')];
      let index = tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      this.selectPanel(tab.closest('dialog'), tabs[index].dataset.reviewTab);
      tabs[index].focus();
    }

    updateComparison(event) {
      if (!event.target.matches('[data-review-range]')) return;
      const value = Math.max(0, Math.min(100, Number(event.target.value)));
      const comparison = event.target.closest('[data-review-comparison]');
      comparison.style.setProperty('--comparison-position', `${value}%`);
      comparison.classList.toggle('is-all-after', value <= 4);
      comparison.classList.toggle('is-all-before', value >= 96);
      event.target.setAttribute('aria-valuetext', `${value}% before image visible`);
    }

    updateMenuControls() {
      const track = this.querySelector('[data-review-menu]');
      if (!track) return;
      const maximum = track.scrollWidth - track.clientWidth;
      this.querySelectorAll('[data-menu-step]').forEach((button) => {
        button.hidden = maximum < 3;
      });
      const menuBar = this.querySelector('.sa-reviews__menu');
      if (menuBar) this.style.setProperty('--reviews-menu-height', `${menuBar.getBoundingClientRect().height}px`);
    }

    updateResultsControls() {
      this.querySelectorAll('[data-results-gallery]').forEach((gallery) => {
        const track = gallery.querySelector('[data-review-results]');
        const controls = gallery.querySelector('[data-results-controls]');
        const maximum = track.scrollWidth - track.clientWidth;
        const position = Math.abs(track.scrollLeft);
        controls.hidden = track.clientWidth === 0 || maximum < 3;
        controls.querySelector('[data-results-step="-1"]').disabled = position < 3;
        controls.querySelector('[data-results-step="1"]').disabled = position >= maximum - 3;
        if (track.matches('[data-review-stories]') && track.clientWidth > 0) {
          const stories = [...track.children];
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          const index = Math.max(0, Math.min(stories.length - 1, Math.round(position / (track.clientWidth + gap))));
          const story = stories[index];
          if (!story) return;
          gallery.classList.toggle('has-story-photos', story.classList.contains('sa-reviews__story-content--with-photos'));
          const body = track.closest('[data-review-scroll]');
          const bodyStyle = getComputedStyle(body);
          const photoSpace = `${Math.max(0, body.clientHeight - parseFloat(bodyStyle.paddingTop) - parseFloat(bodyStyle.paddingBottom))}px`;
          if (gallery.style.getPropertyValue('--reviews-story-photo-space') !== photoSpace) {
            gallery.style.setProperty('--reviews-story-photo-space', photoSpace);
          }
          // Fit the current note, including space for a non-overlay horizontal scrollbar.
          const scrollbar = track.offsetHeight - track.clientHeight;
          const height = `${Math.ceil(story.getBoundingClientRect().height) + scrollbar}px`;
          if (track.style.height !== height) track.style.height = height;
          const status = controls.querySelector('[data-story-position]');
          const label = `Story ${index + 1} of ${stories.length}`;
          if (status && status.textContent !== label) status.textContent = label;
        }
      });
    }

    changeWrittenReview(gallery, step) {
      const pages = [...gallery.querySelectorAll('[data-written-review]')];
      const current = pages.findIndex((page) => !page.hidden);
      const index = Math.max(0, Math.min(pages.length - 1, current + step));
      if (index === current || !pages[index]) return;
      pages.forEach((page, position) => { page.hidden = position !== index; });
      pages[index].querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
      gallery.querySelector('[data-written-position]').textContent = `Review ${index + 1} of ${pages.length}`;
      gallery.querySelector('[data-written-step="-1"]').disabled = index === 0;
      gallery.querySelector('[data-written-step="1"]').disabled = index === pages.length - 1;
      this.updateProgramCards();
      const body = gallery.closest('[data-review-scroll]');
      const dialog = body.closest('dialog');
      if (window.matchMedia('(max-width: 799px)').matches) {
        dialog.scrollTop += body.getBoundingClientRect().top - dialog.getBoundingClientRect().top;
      } else body.scrollTop = 0;
    }

    updateProgramCards() {
      this.dialogs.forEach((dialog) => {
        if (!dialog.open || !dialog.querySelector('[data-review-panel="review"]:not([hidden]) [data-written-review]:not([hidden]) .sa-reviews__program-card')) return;
        const body = dialog.querySelector('[data-review-scroll]');
        const header = dialog.querySelector('.sa-reviews__dialog-header');
        const bodyStyle = getComputedStyle(body);
        // Use the fixed dialog's available height, not content height, so short reviews cannot shrink the image repeatedly.
        const available = dialog.clientHeight - header.getBoundingClientRect().height - parseFloat(bodyStyle.paddingTop) - parseFloat(bodyStyle.paddingBottom);
        const photoSpace = `${Math.max(0, available)}px`;
        if (dialog.style.getPropertyValue('--reviews-program-photo-space') !== photoSpace) {
          dialog.style.setProperty('--reviews-program-photo-space', photoSpace);
        }
      });
    }

    async playVideo(button) {
      const media = button.closest('[data-review-row]').querySelector('[data-review-media]');
      if (!media || media.classList.contains('is-loading')) return;
      const { signal } = this.controller;
      const status = media.querySelector('[data-video-status]');
      const playButton = media.querySelector('.sa-reviews__play');
      let video = media.querySelector('video');
      if (!video) {
        media.append(media.querySelector('template').content.cloneNode(true));
        video = media.querySelector('video');
        video.tabIndex = -1;
        video.addEventListener('playing', () => {
          if (video.paused) return;
          media.classList.remove('is-loading');
          media.classList.add('is-playing');
          playButton.hidden = true;
          media.querySelector('[data-close-video]').hidden = false;
          video.tabIndex = 0;
        }, { signal });
        video.addEventListener('ended', () => this.closeVideo(media), { signal });
        video.addEventListener('error', () => this.videoError(media), { signal });
        const sources = [...video.querySelectorAll('source')];
        const failedSources = new Set();
        video.addEventListener('loadstart', () => failedSources.clear(), { signal });
        sources.forEach((source) => source.addEventListener('error', () => {
          failedSources.add(source);
          // Chromium can leave play() pending when every <source> fails, without a video error event.
          if (video.networkState === 3 || sources.every((item) => failedSources.has(item) || (item.type && !video.canPlayType(item.type)))) {
            this.videoError(media);
          }
        }, { signal }));
      }
      // Pause the existing carousel too, so two testimonials never speak over each other.
      document.querySelectorAll('video').forEach((other) => { if (other !== video) other.pause(); });
      status.textContent = '';
      // Expose the native player immediately, including when iOS needs another play tap.
      // Keep load() and play() in the original user gesture, before any awaited work.
      media.classList.add('is-loading', 'is-video-open');
      playButton.hidden = true;
      media.querySelector('[data-close-video]').hidden = false;
      video.tabIndex = 0;
      const bounds = media.getBoundingClientRect();
      if (bounds.bottom > window.innerHeight || bounds.top < 0) {
        media.scrollIntoView({ block: 'center', behavior: window.matchMedia('(max-width: 799px)').matches ? 'instant' : this.scrollBehavior() });
      }
      const playbackRequest = (media.playbackRequest || 0) + 1;
      media.playbackRequest = playbackRequest;
      try {
        video.playsInline = true;
        video.muted = false;
        if (video.error || video.networkState === 0 || video.networkState === 3) video.load();
        if (video.ended) video.currentTime = 0;
        await video.play();
        if (media.playbackRequest !== playbackRequest) return;
        if (signal.aborted) { video.pause(); return; }
        if (video.paused) return;
        video.focus({ preventScroll: true });
      } catch (error) {
        if (signal.aborted || media.playbackRequest !== playbackRequest) return;
        if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
          media.classList.remove('is-loading');
          // Leave native controls usable instead of hiding the player behind its portrait.
        }
        else this.videoError(media);
      }
    }

    closeVideo(media) {
      media.playbackRequest = (media.playbackRequest || 0) + 1;
      const video = media.querySelector('video');
      const closeButton = media.querySelector('[data-close-video]');
      const restoreFocus = document.activeElement === video || document.activeElement === closeButton;
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.tabIndex = -1;
      }
      media.classList.remove('is-loading', 'is-playing', 'is-video-open');
      closeButton.hidden = true;
      const playButton = media.querySelector('.sa-reviews__play');
      playButton.hidden = false;
      media.querySelector('[data-video-status]').textContent = '';
      if (restoreFocus) playButton.focus({ preventScroll: true });
    }

    videoError(media) {
      media.classList.remove('is-loading', 'is-playing', 'is-video-open');
      media.querySelector('.sa-reviews__play').hidden = false;
      media.querySelector('[data-close-video]').hidden = true;
      const video = media.querySelector('video');
      if (video) { video.pause(); video.tabIndex = -1; }
      media.querySelector('[data-video-status]').textContent = 'The video couldn’t load. Please try playing it again.';
    }

    isOutside(event, dialog) {
      const bounds = dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    scrollBehavior() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }

    followHash(focus = true, hash = window.location.hash) {
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
      if (!id) return;
      const target = document.getElementById(id);
      if (!target || !this.contains(target) || !target.matches('[data-review-row]')) return;
      const styles = getComputedStyle(this);
      // Explicitly combine header and extra spacing to avoid double counting theme scroll-padding.
      const spacer = document.createElement('span');
      spacer.style.cssText = 'position:absolute;visibility:hidden;height:var(--reviews-sticky-height);';
      this.append(spacer);
      const headerHeight = spacer.getBoundingClientRect().height;
      spacer.remove();
      const menuHeight = this.querySelector('.sa-reviews__menu')?.getBoundingClientRect().height || 0;
      const extra = parseFloat(styles.getPropertyValue('--reviews-anchor-offset')) || 0;
      // Land on the visible content, rather than counting the row's top padding as extra space.
      const content = [...target.children].filter((child) => child.matches('.sa-reviews__copy, .sa-reviews__media'));
      const contentTop = content.length ? Math.min(...content.map((child) => child.getBoundingClientRect().top)) : target.getBoundingClientRect().top;
      window.scrollTo({ top: window.scrollY + contentTop - headerHeight - menuHeight - extra, behavior: focus ? this.scrollBehavior() : 'instant' });
      if (focus) target.focus({ preventScroll: true });
      this.querySelectorAll('[data-review-menu] a').forEach((link) => {
        if (link.hash.slice(1) === id) {
          link.setAttribute('aria-current', 'location');
          const track = link.closest('[data-review-menu]');
          const bounds = track.getBoundingClientRect();
          const item = link.getBoundingClientRect();
          const offset = item.left < bounds.left + 6 ? item.left - bounds.left - 6 : item.right > bounds.right - 6 ? item.right - bounds.right + 6 : 0;
          if (offset) track.scrollBy({ left: offset, behavior: 'instant' });
        }
        else link.removeAttribute('aria-current');
      });
    }
  });
}
